import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';

export default function Onboard() {
  const { user, refreshOwnerStatus } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registerAsOwner = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      await setDoc(doc(db, 'clients', user.uid), { createdAt: new Date().toISOString() }, { merge: true });
      await refreshOwnerStatus();
      navigate('/');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to register. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
      p: 2,
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: 420,
        bgcolor: 'background.paper',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        p: { xs: 3, sm: 4 },
        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
      }}>
        <Typography
          variant="h4"
          sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 400, mb: 0.5 }}
        >
          Welcome
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          How will you be using Utilibill?
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

        <Stack spacing={2}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={loading}
            startIcon={loading ? undefined : <HomeWorkOutlinedIcon />}
            onClick={() => void registerAsOwner()}
            sx={{ py: 1.75, justifyContent: 'flex-start', px: 2.5 }}
          >
            {loading
              ? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />Setting up…</>
              : 'I manage properties'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            disabled={loading}
            startIcon={<PersonOutlineIcon />}
            onClick={() => navigate('/tenant/sign-in')}
            sx={{ py: 1.75, justifyContent: 'flex-start', px: 2.5 }}
          >
            I'm a tenant
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
